<style>
  tr:hover td {
      background-color: #3F51B5;
  }
    .attachedYesColor {color: #0f9e3e !important;}
    .attachedNoColor {color: #9e9e9e !important;}
    .feedbackSnapshot {
      background-color: #f5f5f5;
      border: 1px solid #dddddd;
      border-radius: 4px;
      padding: 10px;
      font-size: 13px;
      max-height: 420px;
      overflow-y: auto;
      color: #212529;
    }
    /* The admin theme styles bare <pre> for dark cards, which lands light grey on this
       near-white box and is unreadable. Force the dark colour back on. */
    .feedbackSnapshot pre {
      color: #212529 !important;
      background: transparent !important;
      border: 0 !important;
      font-size: 13px;
      line-height: 1.55;
      margin: 0;
    }
    .feedbackLabel {
      color: #212529;
      font-weight: 600;
    }
    .feedbackMeta {color: #495057 !important;}
</style>
<template>
  <div>
	<h1 class="page-header mb-3">Chat App Feedback</h1>
  <div class="row">
      <div class="col-xl-12">
        <div class="card border-0 mb-3 overflow-hidden bg-gray-800 text-white">
          <div class="card-body">
            <p>In-app feedback submitted by chat users, including the question and answer the tester attached to the report.</p>
            <div v-if="loading" class="loading text-center"><div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div></div>
            <div v-if="!loading && rows && !rows.length" class="alert alert-warning">
              No feedback has been submitted yet.
            </div>
            <vue-good-table
              :columns="columns"
              v-if="rows && rows.length"
                :rows="rows"
              :lineNumbers="false"
              :search-options="{ enabled: true, skipDiacritics: true, placeholder: 'Search Current Table' }"
              :pagination-options="{ enabled: true,  position: 'bottom', perPage: 20, mode: 'pages' }"
              :selectOptions="{
                enabled: false,
                selectOnCheckboxOnly: true,
                selectionInfoClass: 'alert alert-info m-b-0 no-rounded-corner',
                selectionText: 'rows selected',
                clearSelectionText: 'clear',
              }"
              :sort-options="{
                enabled: true,
                multipleColumns: true
              }"
            >
              <div slot="table-actions">
                <span class="me-2">{{ rows.length }} of {{ total }} submissions</span>
                <button class="btn btn-primary ms-2 fw-bold" @click="fetchData">REFRESH <span class="fa fa-refresh"></span></button>
              </div>
              <template slot="table-row" slot-scope="props">
                  <span v-if="props.column.field === 'actions'">
                    <button class="btn btn-sm btn-outline-info" @click.stop="viewFeedback(props.row)">View</button>
                  </span>
                  <span v-else>{{props.formattedRow[props.column.field]}}</span>
              </template>
            </vue-good-table>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="feedback-modal" size="lg" title="Feedback Detail" cancel-variant="default" scrollable ok-only ok-title="Close">
      <div v-if="selected">
        <p class="mb-1">
          <strong>{{ selected.firstName }} {{ selected.lastName }}</strong>
          <span v-if="selected.username"> ({{ selected.username }})</span>
        </p>
        <p class="mb-1 feedbackMeta">{{ selected.email }}</p>
        <p class="mb-1 feedbackMeta">{{ formatDate(selected.createdAt) }}</p>
        <p class="mb-3 feedbackMeta" v-if="selected.conversationId">
          Conversation #{{ selected.conversationId }}<span v-if="selected.conversationTitle"> &mdash; {{ selected.conversationTitle }}</span>
        </p>

        <h6 class="fw-bold feedbackLabel">Feedback</h6>
        <div class="feedbackSnapshot mb-3"><pre class="mb-0" style="white-space: pre-wrap; word-break: break-word;">{{ selected.message }}</pre></div>

        <div v-if="selected.promptSnapshot">
          <h6 class="fw-bold feedbackLabel">Attached Question</h6>
          <div class="feedbackSnapshot mb-3"><pre class="mb-0" style="white-space: pre-wrap; word-break: break-word;">{{ selected.promptSnapshot }}</pre></div>
        </div>

        <div v-if="selected.answerSnapshot">
          <h6 class="fw-bold feedbackLabel">Attached Answer</h6>
          <div class="feedbackSnapshot mb-0"><pre class="mb-0" style="white-space: pre-wrap; word-break: break-word;">{{ selected.answerSnapshot }}</pre></div>
        </div>

        <p class="mb-0 text-muted" v-if="!selected.promptSnapshot && !selected.answerSnapshot">
          No question or answer was attached to this report.
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { useCookies } from "vue3-cookies";
import axios from "axios";
import moment from 'moment';
import ChatApi from '../config/ChatApi.vue';


export default {
  name: 'chatfeedback',
    setup() {
      const { cookies } = useCookies();
      return { cookies };
    },
    data()
    {
      return {
        loading: false,
        selected: null,
        total: 0,
        columns: [
          {
            label: 'Date',
            field: 'createdAt',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.formatDate,
          },
          {
            label: 'User',
            field: 'username',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap'
          },
          {
            label: 'Email',
            field: 'email',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap'
          },
          {
            label: 'Feedback',
            field: 'message',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            formatFn: this.excerpt,
          },
          {
            label: 'Attached',
            field: 'attached',
            thClass: 'text-nowrap',
            tdClass: this.getAttachedColor,
          },
          {
            label: 'Actions',
            field: 'actions',
            thClass: 'text-nowrap',
            tdClass: 'text-nowrap',
            sortable: false,
          }
        ],
        rows: null,
      }
    },
    methods: {
      fetchData()
      {
        this.error = null;
        this.loading = true;

        axios
          .get(ChatApi.base + '/api/admin/feedback', {
            headers: { 'x-admin-key': ChatApi.key },
            params: { limit: 300 }
          })
          .then(response => {
              let feedback = response.data.feedback || [];
              feedback.forEach(function (item) {
                item.attached = (item.promptSnapshot || item.answerSnapshot) ? 'Yes' : 'No';
              });
              this.rows = feedback;
              this.total = response.data.total || feedback.length;
              this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            this.$swal.fire('Error', 'Could not load chat feedback. Check the chat app admin key and try again.', 'error');
            console.log(error);
          });
      },
      viewFeedback(row)
      {
        this.selected = row;
        this.$bvModal.show('feedback-modal');
      },
      formatDate(value)
      {
        if (!value) return '';
        return moment(value).format('MMM D, YYYY h:mm a');
      },
      excerpt(value)
      {
        if (!value) return '';
        let s = String(value).replace(/\s+/g, ' ').trim();
        return s.length > 90 ? s.substring(0, 90) + '...' : s;
      },
      getAttachedColor(rowObj)
      {
        return rowObj.attached === 'Yes' ? 'attachedYesColor' : 'attachedNoColor';
      }
    },
    mounted: function()
    {
      let username = this.cookies.get('username');
      let authenticated = this.cookies.get("authenticated");
      if (username === null || authenticated === null)
      {
        this.$router.push('/user/login-v3');
      }
      else {
        const crypto = require('crypto');
        const secret = 'GASEWkjshga@asdjfhasdfAA!fcaf@gdh#asdA';
        const hash = crypto.createHmac('sha256', secret)
            .update(authenticated)
            .digest('hex');
        if (username !== 'admin' || hash !== 'b8d32bd79ea384bb101f69e0935ee9c87be516c6642e39a479bf417b16354b2e') {
          this.$router.push('/user/login-v3');
        } else {
          this.fetchData();
        }
      }
    }
	}
</script>
